import {
  Gear, EnvelopeSimple, CheckCircle, XCircle, GoogleDriveLogo, Info, ArrowsClockwise, FloppyDisk,
} from "@phosphor-icons/react";

export default function BackupConfigSection({
  config, cfgDraft, setCfgDraft, savingCfg, testing, testResult, onSave, onTest,
}) {
  return (
    <section className="border border-black bg-white mb-8" data-testid="backup-config">
      <div className="border-b border-black px-6 py-4 flex items-center gap-2">
        <Gear size={18} weight="bold" />
        <div className="font-head font-bold text-lg">Configurações de Backup Automático</div>
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Coluna esquerda: agendamento + email */}
        <div className="space-y-4">
          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1"
                checked={cfgDraft.auto_enabled}
                onChange={(e) => setCfgDraft({ ...cfgDraft, auto_enabled: e.target.checked })}
                data-testid="backup-auto-enabled"
              />
              <div>
                <div className="font-bold text-sm">Ativar backup automático diário</div>
                <div className="text-xs text-zinc-500">Roda todo dia no horário configurado (fuso Brasília).</div>
              </div>
            </label>
          </div>

          <div>
            <label className="label">Horário diário (America/Sao_Paulo)</label>
            <input
              type="time"
              className="input font-mono"
              value={cfgDraft.schedule_time}
              onChange={(e) => setCfgDraft({ ...cfgDraft, schedule_time: e.target.value })}
              disabled={!cfgDraft.auto_enabled}
              data-testid="backup-schedule-time"
            />
          </div>

          <div>
            <label className="label">E-mails destinatários (separados por vírgula)</label>
            <textarea
              className="input font-mono text-xs"
              rows={2}
              placeholder="admin@empresa.com, diretoria@empresa.com"
              value={cfgDraft.email_recipients}
              onChange={(e) => setCfgDraft({ ...cfgDraft, email_recipients: e.target.value })}
              data-testid="backup-email-recipients"
            />
            <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1">
              <EnvelopeSimple size={12} />
              Remetente fixo: <span className="font-mono">{config?.email_configured ? "admpopcopos@gmail.com" : "não configurado"}</span>
              {config?.email_configured && <CheckCircle size={12} weight="fill" className="text-[#00A300]" />}
            </div>
          </div>
        </div>

        {/* Coluna direita: Drive */}
        <div className="space-y-4">
          <div className="border border-zinc-200 p-4 bg-zinc-50 dark:bg-zinc-900/40">
            <div className="flex items-center gap-2 mb-3">
              <GoogleDriveLogo size={18} weight="bold" />
              <div className="font-bold text-sm">Google Drive (Service Account)</div>
              {config?.drive_configured && <CheckCircle size={14} weight="fill" className="text-[#00A300]" />}
            </div>
            <div>
              <label className="label">Folder ID (da URL do Drive)</label>
              <input
                className="input font-mono text-xs"
                placeholder="1AbCdEfGhIjKlMnOpQrStUvWxYz"
                value={cfgDraft.drive_folder_id}
                onChange={(e) => setCfgDraft({ ...cfgDraft, drive_folder_id: e.target.value })}
                data-testid="backup-drive-folder"
              />
            </div>
            <div className="mt-3">
              <label className="label">JSON da Service Account</label>
              <textarea
                className="input font-mono text-[10px]"
                rows={5}
                placeholder={config?.drive_configured ? "*** já configurado — cole novo JSON para substituir ***" : '{ "type": "service_account", "project_id": "...", ... }'}
                value={cfgDraft.drive_service_account_json}
                onChange={(e) => setCfgDraft({ ...cfgDraft, drive_service_account_json: e.target.value })}
                data-testid="backup-drive-json"
              />
              {config?.drive_service_account_email && (
                <div className="text-[11px] text-zinc-500 mt-1">
                  Ativo como: <span className="font-mono">{config.drive_service_account_email}</span>
                </div>
              )}
              <div className="text-[10px] text-zinc-500 mt-2 flex items-start gap-1">
                <Info size={11} className="mt-0.5 flex-shrink-0" />
                <span>Não esqueça de <strong>compartilhar a pasta do Drive</strong> com o e-mail da service account como <strong>Editor</strong>.</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-black px-6 py-4 flex flex-wrap items-center gap-2 justify-end bg-zinc-50 dark:bg-zinc-900/40">
        <button
          className="btn btn-ghost"
          onClick={onTest}
          disabled={testing}
          data-testid="backup-test-btn"
        >
          <ArrowsClockwise size={14} weight="bold" /> {testing ? "Testando..." : "Testar conexões"}
        </button>
        <button
          className="btn btn-primary"
          onClick={onSave}
          disabled={savingCfg}
          data-testid="backup-config-save"
        >
          <FloppyDisk size={14} weight="bold" /> {savingCfg ? "Salvando..." : "Salvar Configuração"}
        </button>
      </div>

      {testResult && (
        <div className="border-t border-black px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <div className={`p-3 border ${testResult.drive.ok ? "border-[#00A300] bg-[#00A300]/10" : "border-[#E60000] bg-[#E60000]/10"}`}>
            <div className="font-bold flex items-center gap-1">
              {testResult.drive.ok ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}
              Google Drive
            </div>
            <div className="mt-1 break-all">{testResult.drive.message}</div>
          </div>
          <div className={`p-3 border ${testResult.email.ok ? "border-[#00A300] bg-[#00A300]/10" : "border-[#E60000] bg-[#E60000]/10"}`}>
            <div className="font-bold flex items-center gap-1">
              {testResult.email.ok ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}
              E-mail SMTP
            </div>
            <div className="mt-1 break-all">{testResult.email.message}</div>
          </div>
        </div>
      )}
    </section>
  );
}
